/**
 * useRaceStateMachine Hook
 *
 * Manages race phase transitions with a predictable state machine pattern.
 * Replaces scattered phase transitions in useRaceHost with a centralized reducer.
 */

import { useReducer, useCallback } from 'react';
import type { RaceRoom, RaceHostPhase } from '@/types/race';

// State shape
export interface RaceStateMachineState {
  phase: RaceHostPhase;
  room: RaceRoom | null;
  countdown: number | null;
}

// Action types
export type RaceStateMachineAction =
  | { type: 'CREATE_ROOM'; room: RaceRoom }
  | { type: 'RESUME_ROOM'; room: RaceRoom; phase: 'waiting' | 'racing' }
  | { type: 'START_COUNTDOWN' }
  | { type: 'COUNTDOWN_TICK' }
  | { type: 'RACE_STARTED'; room: RaceRoom }
  | { type: 'START_RACE_FAILED' }
  | { type: 'RACE_ENDED' }
  | { type: 'RESET' };

// Initial state
const initialState: RaceStateMachineState = {
  phase: 'configure',
  room: null,
  countdown: null,
};

/**
 * Reducer for race state transitions.
 * All phase transitions are handled here, making the flow predictable and testable.
 */
function raceReducer(
  state: RaceStateMachineState,
  action: RaceStateMachineAction
): RaceStateMachineState {
  switch (action.type) {
    case 'CREATE_ROOM':
      return {
        ...state,
        phase: 'waiting',
        room: action.room,
        countdown: null,
      };

    case 'RESUME_ROOM':
      return {
        ...state,
        phase: action.phase,
        room: action.room,
        countdown: null,
      };

    case 'START_COUNTDOWN':
      return {
        ...state,
        phase: 'countdown',
        countdown: 3,
      };

    case 'COUNTDOWN_TICK':
      if (state.countdown === null || state.countdown <= 1) {
        // Countdown complete - transition handled by RACE_STARTED
        return { ...state, countdown: null };
      }
      return { ...state, countdown: state.countdown - 1 };

    case 'RACE_STARTED':
      return {
        ...state,
        phase: 'racing',
        room: action.room,
        countdown: null,
      };

    case 'START_RACE_FAILED':
      // Go back to waiting if race start failed
      return {
        ...state,
        phase: 'waiting',
        countdown: null,
      };

    case 'RACE_ENDED':
      return {
        ...state,
        phase: 'results',
        countdown: null,
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

/**
 * Hook for managing race phase transitions.
 *
 * @returns State and dispatch function for race phases
 */
export function useRaceStateMachine() {
  const [state, dispatch] = useReducer(raceReducer, initialState);

  // Convenience actions
  const createRoom = useCallback((room: RaceRoom) => {
    dispatch({ type: 'CREATE_ROOM', room });
  }, []);

  const resumeRoom = useCallback((room: RaceRoom, phase: 'waiting' | 'racing') => {
    dispatch({ type: 'RESUME_ROOM', room, phase });
  }, []);

  const startCountdown = useCallback(() => {
    dispatch({ type: 'START_COUNTDOWN' });
  }, []);

  const countdownTick = useCallback(() => {
    dispatch({ type: 'COUNTDOWN_TICK' });
  }, []);

  const raceStarted = useCallback((room: RaceRoom) => {
    dispatch({ type: 'RACE_STARTED', room });
  }, []);

  const startRaceFailed = useCallback(() => {
    dispatch({ type: 'START_RACE_FAILED' });
  }, []);

  const raceEnded = useCallback(() => {
    dispatch({ type: 'RACE_ENDED' });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    // State
    phase: state.phase,
    room: state.room,
    countdown: state.countdown,

    // Actions
    createRoom,
    resumeRoom,
    startCountdown,
    countdownTick,
    raceStarted,
    startRaceFailed,
    raceEnded,
    reset,

    // Raw dispatch for advanced usage
    dispatch,
  };
}
